import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import Button from "../../atoms/button";
import type { TournamentApplyInput } from "../../../types/tournament.types";
import { cn } from "../../../lib/utils/cn";
import { formatDate } from "../../../lib/utils/dateUtils";
import { useTournamentCalendarsList } from "../../../@apis/hooks/useTournament";
import {
  genderLabel,
  unavailableScheduleMessage,
} from "../../../lib/utils/tournamentUtils";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore";

interface CompetitionScheduleScreenProps {
  tournamentId: string | number;
  selectedDate: string;
  onSelectCalendar?: ({
    id,
    date,
    timeType,
  }: {
    id: number;
    date: string;
    timeType: string;
  }) => void;
  onEmptyChange?: (empty: boolean) => void;
}

export default function CompetitionScheduleScreen({
  tournamentId,
  selectedDate,
  onSelectCalendar,
  onEmptyChange,
}: CompetitionScheduleScreenProps) {
  const { setValue, watch } = useFormContext<TournamentApplyInput>();
  const { myInfo } = useMyInfoStore();

  const { data: response, isFetched } = useQuery({
    ...useTournamentCalendarsList(tournamentId, selectedDate),
    enabled: !!tournamentId && !!selectedDate,
  });

  const list = response?.data ?? [];
  const isEmpty = isFetched && list.length === 0;

  useEffect(() => {
    onEmptyChange?.(isEmpty);
  }, [isEmpty, onEmptyChange]);

  // 선택 가능한 일정이 1건이면 선택된 상태로 둔다. 그 1건이 마감이면 선택하지 않는다.
  useEffect(() => {
    if (list.length !== 1) return;
    const entry = list[0];
    const full =
      entry.isFull ??
      (entry.maxApplyCount != null &&
        entry.currentApplyCount >= entry.maxApplyCount);
    if (full) return;
    setValue("calendarId", entry.id);
    onSelectCalendar?.({
      id: entry.id,
      date: entry.date,
      timeType: entry.timeType,
    });
  }, [list, setValue, onSelectCalendar]);

  return (
    <section className="space-y-[15px]">
      <h1 className="font-semibold  mb-[36px]">
        참가하실 대회 일정를 선택해 주세요.
      </h1>

      <p className="text-[#8e8e93] font-semibold text-sm flex gap-x-[4px] items-center mb-[8px]">
        <img src="/icons/ic-calendarcheck.webp" className="size-5" />
        선택 날짜
      </p>
      {selectedDate && (
        <p className="text-base font-semibold mb-[16px]">
          {formatDate(selectedDate, "MM월 DD일")}
        </p>
      )}
      {isEmpty ? (
        <p className="text-sm font-medium text-[#8e8e93] break-keep">
          {unavailableScheduleMessage(myInfo.gender)}
        </p>
      ) : (
      <div className="space-y-[20px]">
        {list.map((entry) => {
          // 정원 마감 여부 (isFull 우선, 없으면 현재/최대 비교)
          const full =
            entry.isFull ??
            (entry.maxApplyCount != null &&
              entry.currentApplyCount >= entry.maxApplyCount);

          return (
            <Button
              key={entry.id}
              className={cn(
                "py-[12px] px-[20px] w-full",
                watch("calendarId") == entry.id && "border-1 border-black",
                // 마감: 회색 배경 + 선택 불가
                full && "bg-[#f2f2f7] cursor-not-allowed",
              )}
              variant={"white"}
              label=""
              onClick={() => {
                if (full) return;
                setValue("calendarId", entry.id);
                onSelectCalendar?.({
                  id: entry.id,
                  date: entry.date,
                  timeType: entry.timeType,
                });
              }}
            >
              <div className="flex justify-between items-center w-full gap-x-[8px]">
                <p
                  className={cn(
                    "text-sm font-medium flex gap-x-[8px] items-center w-full whitespace-pre-line break-all",
                    full && "opacity-30",
                  )}
                >
                  <span
                    className={cn("px-[4px] rounded-[5px] whitespace-nowrap", {
                      "bg-[#fff8da] text-[#a2845e]": entry.gender === null,
                      "bg-[#ffdada] text-[#ff3b30]": entry.gender === 0,
                      "bg-[#daeaff] text-[#007aff]": entry.gender === 1,
                    })}
                  >
                    {genderLabel(entry.gender as number)}
                  </span>
                  {entry.timeType}
                </p>

                {(entry.maxApplyCount ?? 0) < 100000 && (
                  <small
                    className={cn(
                      "text-black text-sm font-medium shrink-0",
                      full && "opacity-30",
                    )}
                  >
                    <span
                      className={full ? "text-[#ff3b30]" : "text-[#34c759]"}
                    >
                      {entry.currentApplyCount}
                    </span>
                    /{entry.maxApplyCount!.toLocaleString()}명
                  </small>
                )}
              </div>
            </Button>
          );
        })}
      </div>
      )}
    </section>
  );
}
